import { Component } from '@theme/component';

/**
 * @typedef {Object} CollapsibleRefs
 * @property {HTMLElement} content - The container whose height is clamped and animated.
 * @property {HTMLElement} toggle - The button that expands/collapses the content.
 */

/**
 * A custom element that clamps long content to a collapsed height and reveals a
 * "show more / show less" toggle. The toggle is only shown when the content
 * actually overflows the collapsed height, so short content is left untouched.
 *
 * @extends {Component<CollapsibleRefs>}
 */
class CollapsibleContent extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {Animation | undefined} */
  #animation;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @constant {number} */
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();
    // Wait a frame so the browser has laid the content out before measuring.
    requestAnimationFrame(() => this.#evaluate());

    // Re-measure when the layout reflows (e.g. viewport resize changes the text
    // height) so the toggle never gets stuck in the wrong state.
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#evaluate());
      this.#resizeObserver.observe(this);
    }

    // Images inside the description have no height until they load, which would
    // make the content measure too short and wrongly hide the toggle. Re-measure
    // as each one finishes loading.
    for (const image of this.refs.content?.querySelectorAll('img') ?? []) {
      if (!image.complete) {
        image.addEventListener('load', this.#evaluate, { once: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * The height, in pixels, the content is clamped to while collapsed.
   * @type {number}
   */
  get #collapsedHeight() {
    return parseInt(this.dataset.collapsedHeight ?? '0', 10) || 0;
  }

  /**
   * Decides whether the content overflows and therefore needs a toggle.
   */
  #evaluate = () => {
    const { content, toggle } = this.refs;
    const overflows = content.scrollHeight - 1 > this.#collapsedHeight;

    this.toggleAttribute('data-overflows', overflows);

    if (overflows) {
      toggle.hidden = false;
    } else {
      // Nothing to collapse: reveal everything and drop the toggle.
      content.style.maxHeight = 'none';
      toggle.hidden = true;
    }
  };

  /**
   * Expands or collapses the content with a height animation.
   * @param {Event} [event]
   */
  toggle = (event) => {
    event?.preventDefault();

    const { content, toggle } = this.refs;
    const collapsedHeight = this.#collapsedHeight;
    const startHeight = content.offsetHeight;
    const endHeight = this.#expanded ? collapsedHeight : content.scrollHeight;

    this.#animation?.cancel();
    content.style.overflow = 'hidden';

    this.#animation = content.animate(
      { maxHeight: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      this.#expanded = !this.#expanded;
      content.style.maxHeight = this.#expanded ? 'none' : `${collapsedHeight}px`;
      content.style.overflow = '';
      this.dataset.expanded = String(this.#expanded);
      toggle.setAttribute('aria-expanded', String(this.#expanded));
    };
  };
}

if (!customElements.get('collapsible-content')) {
  customElements.define('collapsible-content', CollapsibleContent);
}
